import React from 'react';
import { type TooltipContentProps } from 'recharts';

interface ChartTooltipProps extends Partial<
  Omit<TooltipContentProps<number, string>, 'formatter'>
> {
  formatter?: (value: number) => string;
}

/**
 * Custom tooltip component that uses CSS variables directly.
 * This ensures tooltip colors update correctly when theme changes.
 */
export function ChartTooltip({ active, payload, label, formatter }: ChartTooltipProps) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  return (
    <div className="bg-card border border-border rounded-md px-3 py-2 shadow-lg">
      {label && <p className="text-sm font-medium text-foreground mb-1">{label}</p>}
      {payload.map((entry, index) => {
        const value = entry.value as number;
        const displayValue = formatter ? formatter(value) : value.toLocaleString();
        return (
          <div key={index} className="flex items-center gap-2 text-sm">
            <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: entry.color }} />
            <span className="text-muted-foreground">{entry.name}:</span>
            <span className="text-foreground font-medium">{displayValue}</span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Pie chart tooltip - slightly different format since pie charts show single values
 */
export function PieChartTooltip({ active, payload, formatter }: ChartTooltipProps) {
  const entry = payload?.[0];
  if (!active || !payload || payload.length === 0 || !entry) {
    return null;
  }

  const value = entry.value as number;
  const displayValue = formatter ? formatter(value) : value.toLocaleString();

  return (
    <div className="bg-card border border-border rounded-md px-3 py-2 shadow-lg">
      <div className="flex items-center gap-2 text-sm">
        <span
          className="w-3 h-3 rounded-sm"
          style={{ backgroundColor: entry.payload?.fill || entry.color }}
        />
        <span className="text-muted-foreground">{entry.name}:</span>
        <span className="text-foreground font-medium">{displayValue}</span>
      </div>
    </div>
  );
}
